import { applyEquityComparisonPolicy, resolveEffectiveRateRequest } from '../../../services/advertised-rate-context';
import { BrandService } from '../../../services/brand.service';
import { FruHttpService } from '../../../services/fru-http.service';
import { collectKeywords, resolveProgram } from '../../../services/product-presentation';
import { warmCalculatorRates } from './calculator-rate-warmup';

// Registered as an APP_INITIALIZER in app.module.ts. Kept in its own file so a test can run it without loading the whole
// app module. search defaults to the page's own query string; a test passes one.

// FRU rate prefetch (Ed 8/11, measured on prod): the advertised rate was not even REQUESTED until
// 516ms -- it waited for a component to initialise -- and landed at 1276ms, while the headline had
// already painted at 870ms. The visitor read the intro, then watched the rate appear into it. Ed:
// "i visually noticed the rate loading after the rest of the intro".
//
// Asking during bootstrap moves the request ~200ms earlier and, with the preconnect in index.html,
// the answer arrives before first paint, so the headline carries the rate from the first frame.
//
// Two deliberate properties:
//   - It does NOT return its promise. An APP_INITIALIZER that returns one BLOCKS bootstrap, which
//     would trade a repaint for a slower page. Fire and forget.
//   - It adds no second request and no duplicated bucket logic: it calls the same resolver and the
//     same service the components call, and getRateFinderRates already parks the in-flight
//     observable on window, so the component's later call is a cache hit on THIS request.
export function warmAdvertisedRate(fruHttp: FruHttpService, brand: BrandService, search?: string): () => void {
  return () => {
    try {
      const where = typeof search === 'string' ? search : window.location.search;
      // Review 8/12 (P1): this is a Hope optimisation and must not run on the ORIGINAL form or
      // any other www route -- strict original-arm isolation is the release gate. Same regex main.ts
      // uses to decide a Hope entry.
      if (!/[?&]startAssistant=1(?:&|$)/i.test(where)) { return; }
      const qp = new URLSearchParams(where);
      // FRU-23774: the calculators price the payment on their own reviewed pairs, which they used to ask for only when they
      // rendered, so the payment filled in 1.5 to 3.5 s after the calculator on a slow phone. Asked here, the answer is
      // usually in before the calculator mounts, and peekRateFinderRates shares the request if it isn't.
      warmCalculatorRates(fruHttp, where);
      const req = applyEquityComparisonPolicy(resolveEffectiveRateRequest({
        type: qp.get('type'),
        loanPurpose: qp.get('LoanPurpose') || qp.get('loanPurpose') || qp.get('loanpurpose'),
        formType: qp.get('FormType') || qp.get('formType'),
        lpButton: qp.get('lp_button'),
        // Review 8/12 (P1): signal PARITY with the paint components. They pass pageType (from
        // BrandService) and program; without them the warm-up can resolve a DIFFERENT bucket, which
        // both wastes a request and forfeits the same-request cache hit that makes this worth doing.
        pageType: (brand && brand.data && brand.data.pageType) || '',
        // Review 8/13 (P1, third round): ?program= alone is NOT parity. The paint components
        // take program from hopePresentation, which resolveProgram() derives from type + the
        // COLLECTED AD KEYWORDS -- so `utm_keyword=VA home equity` paints cash_out.va_15yr while
        // this warm-up (program='') warmed cash_out.10yr_fixed: two requests, and the disclosure
        // scope set and then replaced. Derive it exactly the way both paint sites do
        // (rates-form.component.ts:648, chat.component.ts:937 -- same collector, same param list).
        program: resolveProgram(qp.get('type'),
          collectKeywords(qp.get('utm_keyword'), qp.get('keyword'), qp.get('utm_term'), qp.get('query'))) || '',
      }));
      if (req.kind === 'invalid') { return; }
      fruHttp.getRateFinderRates(req.bucket || undefined)
        .subscribe({ error: () => { /* the real paint path owns error reporting */ } });
    } catch (e) { /* a warm-up must never break bootstrap */ }
  };
}
